import { useEffect, useRef, useState } from "react";

/**
 * Polls the deployed index.html to detect new builds.
 * When a new version is detected, shows a non-intrusive banner
 * so users can choose when to update — instead of force-reloading
 * and wiping their sessionStorage (which logs them out of all modules).
 */
const POLL_INTERVAL = 60_000;

let initialHash: string | null = null;

async function fetchCurrentHash(): Promise<string | null> {
  try {
    const res = await fetch(`${window.location.origin}/index.html?_t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) return null;
    const html = await res.text();
    const scripts = html.match(/src="[^"]*\.js[^"]*"/g);
    return scripts ? scripts.join("|") : html.length.toString();
  } catch {
    return null;
  }
}

export function useAppVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const hasDetected = useRef(false);

  useEffect(() => {
    fetchCurrentHash().then((h) => {
      initialHash = h;
    });

    const interval = setInterval(async () => {
      if (hasDetected.current) return;

      const currentHash = await fetchCurrentHash();
      if (!currentHash || !initialHash) return;

      if (currentHash !== initialHash) {
        console.info("[AppVersionCheck] New version available.");
        hasDetected.current = true;
        setUpdateAvailable(true);
      }
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  const applyUpdate = () => {
    window.location.reload();
  };

  return { updateAvailable, applyUpdate };
}
